import { Localized } from "@fluent/react"
import ArrowRightIcon from "@mui/icons-material/ArrowRight"
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from "@mui/material"
import { ChangeEvent, ReactNode, useCallback, useState } from "react"
import { CancelButton } from "../../../../app/components/CancelButton"
import { SaveButton } from "../../../../app/components/SaveButton"
import { useAppDispatch } from "../../../../app/hooks"
import { DocumentNumberTextField } from "../../controls/DocumentNumberTextField"
import { createQMSTemplate } from "../../documentsQmsApi"
import { CompanyQMSDocumentTypesSelect } from "../components/CompanyQMSDocumentTypesSelect"
import { QMSDocumentTemplates } from "../components/QMSDocumentTemplates"
import { QMSLanguage, QMSLanguageSelect } from "../components/QMSLanguageSelect"

const RequiredError = () => (
    <Localized id="field-is-required">
        <span>_Pole jest wymagane</span>
    </Localized>
)

interface CreateDocumentQMSTemplateDialogProps {
    open?: boolean
    onClose?: () => void
    selectedStructureNode?: {
        id: string
        code: string
        name: string
    } | null
}

type CreateQmsMode = "Empty" | "Template"

interface SectionProps {
    title: ReactNode
    children: ReactNode
}

const DialogSection = ({ title, children }: SectionProps) => {
    return (
        <Box
            sx={(theme) => ({
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: "hidden",
                backgroundColor: theme.palette.background.paper,
            })}
        >
            <Box
                sx={(theme) => ({
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1.5,
                    py: 0.75,
                    backgroundColor: theme.palette.grey[100],
                    borderBottom: `1px solid ${theme.palette.divider}`,
                })}
            >
                <ArrowRightIcon fontSize="small" />

                <Typography
                    variant="subtitle2"
                    sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        letterSpacing: 0.2,
                    }}
                >
                    {title}
                </Typography>
            </Box>

            <Box sx={{ p: 2 }}>
                {children}
            </Box>
        </Box>
    )
}

export const CreateDocumentQMSTemplateDialog = ({ open = true, onClose, selectedStructureNode = null }: CreateDocumentQMSTemplateDialogProps) => {
    const dispatch = useAppDispatch()
    const [documentTypeId, setDocumentTypeId] = useState<number | "">("")
    const [documentTypeCode, setDocumentTypeCode] = useState<string>("CUSTOM")
    const [creationMode, setCreationMode] = useState<CreateQmsMode>("Empty")
    const [documentNumber, setDocumentNumber] = useState("")
    const [documentTitle, setDocumentTitle] = useState("")
    const [language, setLanguage] = useState<QMSLanguage>("PL")
    const [template, setTemplate] = useState("")
    const [saving, setSaving] = useState(false)
    const [validationErrors, setValidationErrors] = useState(false)

    const documentTypeError = validationErrors && documentTypeId === ""
    const documentNumberError = validationErrors && documentNumber.trim() === ""
    const documentTitleError = validationErrors && documentTitle.trim() === ""
    const templateError = validationErrors && creationMode === "Template" && template.trim() === ""
    const selectedNodeError = validationErrors && !selectedStructureNode?.id

    const handleCloseButtonClick = useCallback(() => {
        onClose?.()
    }, [onClose])

    const handleCreationModeChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const mode = event.target.value as CreateQmsMode
        setCreationMode(mode)

        if (mode === "Empty") {
            setTemplate("")
        }
    }, [])

    const handleSaveButtonClick = async () => {
        setValidationErrors(true)

        if (!selectedStructureNode?.id || documentTypeId === "" || documentNumber.trim() === "" || documentTitle.trim() === "") {
            return
        }

        const templateId = creationMode === "Template" ? Number(template) : 0
        if (creationMode === "Template" && (!Number.isInteger(templateId) || templateId <= 0)) {
            return
        }

        setSaving(true)
        try {
            const result = await dispatch(createQMSTemplate({
                documentType: documentTypeCode,
                documentNumber: documentNumber.trim(),
                title: documentTitle.trim(),
                templateId,
                language,
                creationMode,
                structureNodeId: selectedStructureNode.id,
            }))

            if (createQMSTemplate.fulfilled.match(result)) {
                handleCloseButtonClick()
            } else {
                console.error(result.payload)
            }
        } finally {
            setSaving(false)
        }
    }

    return (
        <Dialog open={open} fullWidth maxWidth="md" onClose={handleCloseButtonClick}>
            <DialogTitle>
                <Localized id="qms-document-create-template">
                    <span>_Tworzenie szablonu QMS</span>
                </Localized>
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={2.5} sx={{ pt: 0.5 }}>
                    <Grid item xs={12}>
                        <DialogSection
                            title={
                                <Localized id="base-data">
                                    <span>_Dane podstawowe</span>
                                </Localized>
                            }
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        required
                                        disabled
                                        error={selectedNodeError}
                                        helperText={selectedNodeError ? <RequiredError /> : undefined}
                                        value={selectedStructureNode ? `${selectedStructureNode.code ? `${selectedStructureNode.code} - ` : ""}${selectedStructureNode.name}` : ""}
                                        label={
                                            <Localized id="qms-document-selected-node">
                                                <span>_Wybrany node struktury</span>
                                            </Localized>
                                        }
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FormControl fullWidth error={documentTypeError}>
                                        <CompanyQMSDocumentTypesSelect
                                            value={documentTypeId}
                                            onChange={setDocumentTypeId}
                                            onDocumentTypeChange={(documentType) => setDocumentTypeCode(documentType?.code ?? "CUSTOM")}
                                            required
                                            label="Typ dokumentu"
                                        />
                                        {documentTypeError && <FormHelperText><RequiredError /></FormHelperText>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <DocumentNumberTextField
                                        fullWidth
                                        required
                                        value={documentNumber}
                                        onChange={(event) => setDocumentNumber(event.target.value)}
                                        label={
                                            <Localized id="document-number">
                                                <span>_Numer dokumentu</span>
                                            </Localized>
                                        }
                                        documentType={"CUSTOM"}
                                    />
                                    {documentNumberError && <FormHelperText error><RequiredError /></FormHelperText>}
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <QMSLanguageSelect value={language} onChange={setLanguage} />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        helperText={documentTitleError ? <RequiredError /> : undefined}
                                        required
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        value={documentTitle}
                                        error={documentTitleError}
                                        onChange={(event) => setDocumentTitle(event.target.value)}
                                        label={
                                            <Localized id="document-title">
                                                <span>_Tytuł dokumentu</span>
                                            </Localized>
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </DialogSection>
                    </Grid>

                    <Grid item xs={12}>
                        <DialogSection
                            title={
                                <Localized id="document-creation-mode-template">
                                    <span>_Sposób generowania treści</span>
                                </Localized>
                            }
                        >
                            <FormControl fullWidth>
                                <RadioGroup value={creationMode} onChange={handleCreationModeChange}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <Box
                                                sx={(theme) => ({
                                                    border: `1px solid ${creationMode === "Empty" ? theme.palette.secondary.main : theme.palette.divider}`,
                                                    borderRadius: 2,
                                                    p: 2,
                                                    height: "100%",
                                                    backgroundColor: creationMode === "Empty" ? theme.palette.action.selected : theme.palette.background.paper,
                                                })}
                                            >
                                                <FormControlLabel
                                                    value="Empty"
                                                    control={<Radio color="secondary" />}
                                                    label={
                                                        <Box>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                                <Localized id="document-creation-mode-empty">
                                                                    <span>_Pusty</span>
                                                                </Localized>
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Szablon zostanie utworzony bez treści startowej.
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    sx={{ alignItems: "flex-start", m: 0 }}
                                                />
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <Box
                                                sx={(theme) => ({
                                                    border: `1px solid ${creationMode === "Template" ? theme.palette.secondary.main : theme.palette.divider}`,
                                                    borderRadius: 2,
                                                    p: 2,
                                                    height: "100%",
                                                    backgroundColor: creationMode === "Template" ? theme.palette.action.selected : theme.palette.background.paper,
                                                })}
                                            >
                                                <FormControlLabel
                                                    value="Template"
                                                    control={<Radio color="secondary" />}
                                                    label={
                                                        <Box>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                                <Localized id="document-creation-mode-template">
                                                                    <span>_Z szablonu</span>
                                                                </Localized>
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Utwórz szablon na podstawie innego szablonu przypisanego do wybranego noda.
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    sx={{ alignItems: "flex-start", m: 0 }}
                                                />
                                            </Box>
                                        </Grid>

                                        {creationMode === "Template" && (
                                            <Grid item xs={12}>
                                                <QMSDocumentTemplates
                                                    nodeId={selectedStructureNode?.id ?? null}
                                                    value={template}
                                                    onChange={(value) => setTemplate(value)}
                                                    error={templateError}
                                                    helperText={templateError ? <RequiredError /> : undefined}
                                                />
                                            </Grid>
                                        )}
                                    </Grid>
                                </RadioGroup>
                            </FormControl>
                        </DialogSection>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <SaveButton loading={saving} onClick={handleSaveButtonClick} />
                <CancelButton loading={saving} onClick={handleCloseButtonClick} />
            </DialogActions>
        </Dialog>
    )
}